"use client"

import React from "react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import { PULL_TO_MANY_MODES, type PullToManyMode } from "@/lib/pull-to-many"

const MODE_LABELS: Record<PullToManyMode, { label: TranslationKey; hint: TranslationKey }> = {
  Summary: { label: "pull.toMany.summary", hint: "pull.toMany.summaryHint" },
  List: { label: "pull.toMany.list", hint: "pull.toMany.listHint" },
  Rows: { label: "pull.toMany.rows", hint: "pull.toMany.rowsHint" },
  Sheet: { label: "pull.toMany.sheet", hint: "pull.toMany.sheetHint" },
}

/** Modes another field's layout rules out here, each with the field and the reason to show */
export type UnavailableModes = Partial<Record<PullToManyMode, { reason: TranslationKey; field: string }>>

export interface ToManyModeSelectProps {
  value: PullToManyMode
  onChange: (mode: PullToManyMode) => void
  unavailable: UnavailableModes
}

/** The layout chooser on a to-many association's row in the Fields step */
export function ToManyModeSelect({ value, onChange, unavailable }: ToManyModeSelectProps) {
  const { t } = useTranslation()
  const hintFor = (mode: PullToManyMode) => {
    const blocked = unavailable[mode]
    return blocked ? t(blocked.reason, { field: blocked.field }) : t(MODE_LABELS[mode].hint)
  }

  return (
    <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
      {t("pull.toMany.label")}
      <Select value={value} onValueChange={(mode) => onChange(mode as PullToManyMode)}>
        <SelectTrigger className="h-7 w-36 text-xs" aria-label={t("pull.toMany.label")} title={hintFor(value)}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PULL_TO_MANY_MODES.map((mode) => (
            <SelectItem key={mode} value={mode} disabled={unavailable[mode] !== undefined} title={hintFor(mode)} className="text-xs">
              {t(MODE_LABELS[mode].label)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="hidden text-[10px] lg:inline">{hintFor(value)}</span>
    </span>
  )
}
